import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { Trophy, MapPin, Calendar, Clock, Star, ChevronRight, Sparkles, BookOpen, Users, Globe } from 'lucide-react';
import { renderAIContent, AI_CONTENT_CLASSES } from '@/lib/aiContent';
import WCInternalLinks from '@/components/worldcup/WCInternalLinks';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { translateGroupName, translateCountryName, wcMatchSlug } from '@/lib/wcTranslations';
import { sanitizeHtml } from '@/lib/sanitize';

const API_BASE = '';

function CountdownTimer({ targetDate, t }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
  const diff = Math.max(0, new Date(targetDate).getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const blocks = [
    { val: days, label: t('wc.days') }, { val: hours, label: t('wc.hours') },
    { val: mins, label: t('wc.mins') }, { val: secs, label: t('wc.secs') },
  ];
  return (
    <div className="flex gap-3" data-testid="countdown-timer">
      {blocks.map(b => (
        <div key={b.label} className="bg-slate-900/60 backdrop-blur-sm border border-amber-500/20 rounded-xl px-4 py-2 text-center min-w-[72px]">
          <p className="text-2xl md:text-3xl font-black text-amber-400 tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>{String(b.val).padStart(2, '0')}</p>
          <p className="text-[9px] text-amber-400/60 tracking-widest font-semibold">{b.label}</p>
        </div>
      ))}
    </div>
  );
}

function GroupCard({ group, lang, isHighlighted, t }) {
  if (!group || !group.teams) return null;
  // Extract letter from "Group A", "Group Stage - Group A", etc.
  const nameMatch = group.name.match(/Group\s*([A-L])/i);
  const letter = nameMatch ? nameMatch[1].toLowerCase() : group.name.replace(/[^a-zA-Z]/g, '').toLowerCase();
  const path = lang === 'tr' ? `/${lang}/dunya-kupasi-2026/grup/${letter}` : `/${lang}/world-cup-2026/group/${letter}`;
  const displayName = lang === 'tr' ? `Grup ${letter.toUpperCase()}` : `Group ${letter.toUpperCase()}`;
  const standings = group.standings || group.teams || [];
  return (
    <Link to={path} className={`block rounded-xl p-4 border transition-all hover:scale-[1.02] ${
      isHighlighted ? 'bg-gradient-to-br from-red-950/40 to-slate-900 border-red-500/30 hover:border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.1)]'
        : 'bg-slate-900/60 border-white/5 hover:border-amber-500/30'
    }`} data-testid={`group-card-${letter}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{displayName}</h3>
        {isHighlighted && <span className="text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-semibold">TR</span>}
      </div>
      <div className="space-y-1">
        {standings.slice(0, 4).map((tm, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[10px] text-slate-600 w-3 text-right">{tm.rank || i + 1}</span>
            <img src={tm.team_logo} alt="" className="w-4 h-4 rounded-sm" />
            <span className={`text-xs flex-1 ${tm.team_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-400'}`}>{translateCountryName(tm.team_name, lang)}</span>
            <span className="text-[10px] text-slate-500 tabular-nums w-4 text-right">{tm.played || 0}</span>
            <span className={`text-[10px] tabular-nums w-4 text-right font-bold ${(tm.points || 0) > 0 ? 'text-white' : 'text-slate-600'}`}>{tm.points || 0}</span>
          </div>
        ))}
        <div className="flex items-center justify-end gap-3 mt-1 text-[8px] text-slate-600">
          <span>M</span><span>P</span>
        </div>
      </div>
    </Link>
  );
}

function FixtureRow({ match, lang }) {
  const date = match.date ? new Date(match.date) : null;
  const locale = lang === 'tr' ? 'tr-TR' : lang === 'de' ? 'de-DE' : lang === 'fr' ? 'fr-FR' : lang === 'es' ? 'es-ES' : lang === 'pt' ? 'pt-BR' : lang === 'it' ? 'it-IT' : 'en-US';
  const dateStr = date ? date.toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : '';
  const timeStr = date ? date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '';
  const isTurkey = match.home_name === 'Türkiye' || match.away_name === 'Türkiye';
  const wcPath = lang === 'tr' ? 'dunya-kupasi-2026/mac' : 'world-cup-2026/match';
  return (
    <Link to={`/${lang}/${wcPath}/${wcMatchSlug(match.home_name, match.away_name, match.id, lang)}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all hover:bg-slate-800/30 ${
      isTurkey ? 'border-red-500/20 bg-red-500/5' : 'border-white/5 bg-slate-900/30'
    }`}>
      <div className="w-14 text-center shrink-0">
        <p className="text-[11px] text-slate-400 font-medium">{dateStr}</p>
        <p className="text-[10px] text-slate-500">{timeStr}</p>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <img src={match.home_logo} alt="" className="w-4 h-4 rounded-sm" />
          <span className={`text-[13px] truncate ${match.home_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(match.home_name, lang)}</span>
        </div>
        <div className="flex items-center gap-2">
          <img src={match.away_logo} alt="" className="w-4 h-4 rounded-sm" />
          <span className={`text-[13px] truncate ${match.away_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(match.away_name, lang)}</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-[10px] text-slate-500">{match.venue_name}</p>
      </div>
    </Link>
  );
}

function StadiumCard({ venue }) {
  return (
    <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4 min-w-[200px] shrink-0">
      <MapPin className="w-5 h-5 text-emerald-400 mb-2" />
      <p className="text-sm font-semibold text-white">{venue.name}</p>
      <p className="text-xs text-slate-500">{venue.city}, {venue.country}</p>
    </div>
  );
}

function ContenderBadge({ team, lang, t }) {
  const tierColors = {
    favorite: 'border-amber-500/30 bg-amber-500/5 hover:border-amber-500/50',
    contender: 'border-cyan-500/20 bg-cyan-500/5 hover:border-cyan-500/40',
    dark_horse: 'border-violet-500/20 bg-violet-500/5 hover:border-violet-500/40',
  };
  const tierLabels = { favorite: t('wc.favorite'), contender: t('wc.contender'), dark_horse: t('wc.darkHorse') };
  const wcBase = lang === 'tr' ? `/${lang}/dunya-kupasi-2026` : `/${lang}/world-cup-2026`;
  const teamPath = lang === 'tr' ? 'takim' : 'team';
  const slug = _teamSlug(team.name);
  return (
    <Link to={`${wcBase}/${teamPath}/${slug}`} className={`flex items-center gap-3 px-3 py-2 rounded-xl border transition-all ${tierColors[team.tier] || tierColors.contender}`}>
      <img src={team.logo} alt={team.name} className="w-7 h-7 rounded" />
      <div>
        <p className="text-xs font-semibold text-white">{translateCountryName(team.name, lang)}</p>
        <p className="text-[9px] text-slate-500">{tierLabels[team.tier] || team.tier}</p>
      </div>
    </Link>
  );
}

function _teamSlug(name) {
  const map = {
    'Türkiye': 'turkiye', 'Brazil': 'brazil', 'France': 'france', 'Argentina': 'argentina',
    'England': 'england', 'Germany': 'germany', 'Spain': 'spain', 'Portugal': 'portugal',
    'Netherlands': 'netherlands', 'Croatia': 'croatia', 'Belgium': 'belgium', 'USA': 'usa',
    'Mexico': 'mexico', 'Canada': 'canada', 'Japan': 'japan', 'South Korea': 'south-korea',
    'Australia': 'australia', 'Morocco': 'morocco', 'Senegal': 'senegal', 'Uruguay': 'uruguay',
    'Colombia': 'colombia', 'Ecuador': 'ecuador', 'Paraguay': 'paraguay',
    'Switzerland': 'switzerland', 'Austria': 'austria', 'Sweden': 'sweden', 'Norway': 'norway',
    'Scotland': 'scotland', 'Iran': 'iran', 'Saudi Arabia': 'saudi-arabia', 'Qatar': 'qatar',
    'Egypt': 'egypt', 'Tunisia': 'tunisia', 'Algeria': 'algeria', 'Ivory Coast': 'ivory-coast',
    'Ghana': 'ghana', 'Congo DR': 'congo-dr', 'South Africa': 'south-africa',
    'Cape Verde Islands': 'cape-verde', 'New Zealand': 'new-zealand', 'Panama': 'panama',
    'Haiti': 'haiti', 'Bosnia & Herzegovina': 'bosnia', 'Czech Republic': 'czech-republic',
    'Jordan': 'jordan', 'Iraq': 'iraq', 'Uzbekistan': 'uzbekistan', 'Curaçao': 'curacao',
  };
  return map[name] || name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

export default function WorldCupHub() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/worldcup/hub?lang=${currentLang}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [currentLang]);

  if (loading) return <LoadingPage />;
  if (!data) return null;

  const { tournament, groups, turkey, upcoming_matches, venues, contenders, guides, stats, seo, ai_content } = data;
  const mainGroups = groups.filter(g => !g.name.includes('Ranking') && g.name.match(/Group\s*[A-L]/i));
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;
  const fixPath = currentLang === 'tr' ? 'fikstur' : 'fixtures';
  const trPath = currentLang === 'tr' ? 'turkiye' : 'turkey';

  return (
    <div data-testid="worldcup-hub">
      <WCSubmenu />
      <SEOHead title={seo?.title || t('wc.title')} description={seo?.description} canonical={seo?.canonical} hreflang={seo?.hreflang} jsonLd={seo?.jsonld} />

      {/* HERO */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0a1628 30%, #1a472a 70%, #0a1628 100%)' }}>
        <div className="absolute inset-0 opacity-30">
          <img src="https://static.prod-images.emergentagent.com/jobs/2b79e76a-42eb-4df3-ab27-72c6bfd04714/images/e8355a63940e436daa904a3bbf3cce9fa9643fa486c3a786e5d3044aa9994c6c.png" alt="FIFA World Cup 2026" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-slate-950/60" />
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-12 md:py-20">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to={`/${currentLang}`} className="hover:text-slate-200">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-amber-400">{t('wc.breadcrumb')}</span>
          </div>
          <div className="flex items-start justify-between flex-wrap gap-8">
            <div>
              <p className="text-xs text-amber-400/80 tracking-[0.3em] uppercase font-semibold mb-2">{t('wc.subtitle')}</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-none" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('wc.title').split('2026')[0]}<span className="text-amber-400">2026</span>
              </h1>
            </div>
            <CountdownTimer targetDate={tournament.start_date} t={t} />
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            {[
              { icon: Users, val: '48', label: t('wc.teams') },
              { icon: Calendar, val: String(stats.total_fixtures), label: t('wc.matches') },
              { icon: MapPin, val: String(stats.total_venues), label: t('wc.stadiums') },
              { icon: Trophy, val: String(stats.total_groups), label: t('wc.groups') },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2">
                <s.icon className="w-4 h-4 text-amber-400/70" />
                <span className="text-sm font-bold text-white">{s.val}</span>
                <span className="text-xs text-slate-400">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative" style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/2b79e76a-42eb-4df3-ab27-72c6bfd04714/images/3fbfbd264177df041a7caada58774d68835f7a7be0f105ce9e60687d8ff74d53.png)', backgroundAttachment: 'fixed', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <div className="absolute inset-0 bg-slate-950/[0.88] z-0" />
        <div className="container mx-auto px-4 max-w-7xl py-8 space-y-10 relative z-10">

          {/* TURKEY SPOTLIGHT */}
          {turkey?.group && (
            <section data-testid="turkey-spotlight">
              <div className="bg-gradient-to-r from-red-950/40 via-red-950/20 to-slate-900/40 border border-red-500/20 rounded-2xl p-6 md:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 opacity-5">
                  <img src="https://media.api-sports.io/football/teams/777.png" alt="" className="w-full h-full object-contain" />
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <img src="https://media.api-sports.io/football/teams/777.png" alt={t('wc.turkey')} className="w-10 h-10" />
                    <div>
                      <h2 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{t('wc.turkeyGroup')}</h2>
                      <p className="text-xs text-red-400/80">{t('wc.turkeyJourney')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    {turkey.group.teams.map(tm => (
                      <div key={tm.team_id} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${tm.team_name === 'Türkiye' ? 'bg-red-500/15 border border-red-500/30' : 'bg-white/5 border border-white/5'}`}>
                        <img src={tm.team_logo} alt="" className="w-5 h-5" />
                        <span className={`text-sm font-medium ${tm.team_name === 'Türkiye' ? 'text-red-300' : 'text-slate-300'}`}>{translateCountryName(tm.team_name, currentLang)}</span>
                      </div>
                    ))}
                  </div>
                  {turkey.matches.length > 0 && (
                    <div className="space-y-1.5 mb-4">
                      {turkey.matches.map(m => (
                        <div key={m.id} className="flex items-center gap-3 text-sm">
                          <span className="text-xs text-slate-500 w-20">{new Date(m.date).toLocaleDateString(currentLang === 'tr' ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'short' })}</span>
                          <img src={m.home_logo} alt="" className="w-4 h-4" />
                          <span className={`${m.home_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(m.home_name, currentLang)}</span>
                          <span className="text-slate-600">vs</span>
                          <span className={`${m.away_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(m.away_name, currentLang)}</span>
                          <img src={m.away_logo} alt="" className="w-4 h-4" />
                          <span className="text-[10px] text-slate-500 ml-auto">{m.venue_name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-3">
                    <Link to={`${wcBase}/${trPath}`} className="bg-red-600 hover:bg-red-500 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">{t('wc.turkeyPage')}</Link>
                    <Link to={`${wcBase}/${currentLang === 'tr' ? 'grup' : 'group'}/d`} className="bg-white/10 hover:bg-white/15 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors border border-white/10">{t('wc.groupDetail')}</Link>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* CONTENDERS */}
          {contenders.length > 0 && (
            <section data-testid="contenders">
              <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                <Star className="w-5 h-5 inline mr-2 text-amber-400" />{t('wc.contenders')}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {contenders.map(c => <ContenderBadge key={c.team_id} team={c} lang={currentLang} t={t} />)}
              </div>
            </section>
          )}

          {/* GROUPS */}
          <section data-testid="groups-grid">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                <Trophy className="w-5 h-5 inline mr-2 text-amber-400" />{t('wc.groupsLabel').toUpperCase()}
              </h2>
              <Link to={`${wcBase}/${fixPath}`} className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1">
                {t('wc.allFixtures')} <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {mainGroups.map(g => <GroupCard key={g.name} group={g} lang={currentLang} isHighlighted={g.name.includes('D')} t={t} />)}
            </div>
          </section>

          {/* UPCOMING MATCHES */}
          <section data-testid="upcoming-matches">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                <Calendar className="w-5 h-5 inline mr-2 text-amber-400" />{t('wc.upcomingMatches')}
              </h2>
              <Link to={`${wcBase}/${fixPath}`} className="text-xs text-amber-400 hover:text-amber-300">{t('wc.viewAll')}</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {upcoming_matches.map(m => <FixtureRow key={m.id} match={m} lang={currentLang} />)}
            </div>
          </section>

          {/* STADIUMS */}
          <section data-testid="stadiums">
            <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              <MapPin className="w-5 h-5 inline mr-2 text-emerald-400" />{t('wc.stadiumsTitle', t('wc.stadiums')).toUpperCase()}
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {venues.map(v => <StadiumCard key={v.name} venue={v} />)}
            </div>
          </section>

          {/* GUIDES */}
          {guides.length > 0 && (
            <section data-testid="guide-links">
              <h2 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                <BookOpen className="w-5 h-5 inline mr-2 text-cyan-400" />{t('wc.guidesTitle')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {guides.map(g => (
                  <div key={g.slug} className="bg-slate-900/60 border border-white/5 rounded-xl p-4 hover:border-cyan-500/20 transition-all">
                    <BookOpen className="w-4 h-4 text-cyan-400 mb-2" />
                    <p className="text-sm font-semibold text-white mb-1">{g.title}</p>
                    <p className="text-xs text-slate-500">{g.description}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* AI ANALYSIS */}
          <section data-testid="ai-analysis">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-amber-500/20 p-6 md:p-8 shadow-[0_0_20px_rgba(251,191,36,0.05)]">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{t('wc.aiAnalysis')}</h2>
              </div>
              {ai_content?.tournament_analysis ? (
                <div className={AI_CONTENT_CLASSES} dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderAIContent(ai_content.tournament_analysis)) }} />
              ) : (
                <p className="text-sm text-slate-400">{t('wc.aiComingSoon')}</p>
              )}
            </div>
          </section>

          {/* FAVORITES ANALYSIS */}
          {ai_content?.favorites_analysis && (
            <section>
              <div className="bg-slate-900/60 border border-white/5 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-5 h-5 text-amber-400" />
                  <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{t('wc.favAnalysis')}</h2>
                </div>
                <div className={AI_CONTENT_CLASSES} dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderAIContent(ai_content.favorites_analysis)) }} />
              </div>
            </section>
          )}

          {/* ODDS PLACEHOLDER */}
          <section data-testid="odds-placeholder">
            <div className="bg-slate-900/40 border border-dashed border-slate-700/50 rounded-xl p-6 text-center">
              <Globe className="w-6 h-6 text-slate-600 mx-auto mb-2" />
              <p className="text-sm text-slate-500">{t('wc.oddsPlaceholder')}</p>
              <p className="text-xs text-slate-600 mt-1">{t('wc.oddsCompare')}</p>
            </div>
          </section>

          <WCInternalLinks currentPage="hub" lang={currentLang} />
        </div>
      </div>

      {/* BOTTOM HERO */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src="https://static.prod-images.emergentagent.com/jobs/2b79e76a-42eb-4df3-ab27-72c6bfd04714/images/c30710657c2e08ddca11a7dd01c13305daf2a36e7a809fe1ec3fa231aba8f3d1.png" alt="World Cup 2026" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/50 to-slate-950" />
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-12 text-center">
          <p className="text-xs text-amber-400/60 tracking-[0.3em] uppercase font-semibold mb-2">ODDSIS - PROFESSIONAL ODDS COMPARISON</p>
          <h2 className="text-2xl md:text-3xl font-black text-white/90 mb-3" style={{ fontFamily: 'Outfit, var(--font-heading), sans-serif' }}>
            {t('wc.title').split('2026')[0]}<span className="text-amber-400">2026</span>
          </h2>
          <p className="text-sm text-slate-400 max-w-lg mx-auto">{t('wc.bottomTagline')}</p>
        </div>
      </div>
    </div>
  );
}
